'use client'

import type { ReactNode } from 'react'

import { Col, Container, Row } from 'react-bootstrap'

import './Eligibility.css'

import { Fragment } from 'react/jsx-runtime'

interface EligibilityDoc {
  text?: string
  title?: string
  backgroundColor?: string
}
export interface EligibilityProps {
  yearsOld?: string
  title?: string
  highlightedText?: string
  documentsTitle?: string
  documents?: EligibilityDoc[]
  employeeType?: string
  tagline2?: string
  eligibilityTitle?: string
  ageRange?: {
    minimum?: { value?: string; label?: string }
    maximum?: { value?: string; label?: string }
  }
  income?: { range?: string; description?: string; subheading?: ReactNode }
}

const Eligibility = ({
  yearsOld = '',
  title = 'Apply easily',
  highlightedText = 'in just\n2 steps',
  documentsTitle = 'Required Documents',
  documents = [
    {
      text: 'Latest <b>EPF statement</b>',
      backgroundColor: '#DCE9FF'
    },
    {
      text: 'Latest <b>3 months salary slip</b> and personal bank statement reflecting salary crediting',
      backgroundColor: '#F2F4F7'
    }
  ],
  employeeType = '',
  tagline2 = '',
  eligibilityTitle = 'Eligibility',
  ageRange = {
    minimum: {
      value: '21',
      label: 'Minimum Age'
    },
    maximum: {
      value: '35',
      label: 'Maximum Age'
    }
  },
  income = {
    range: 'RM 3,000 - RM 4,999',
    description: 'of minimum monthly gross income'
  }
}: EligibilityProps) => {
  return (
    <section id="eligibility" className="values section h-100">
      <Container>
        <Row className="gy-4">
          <Col
            xl={6}
            xs={12}
            className="d-flex justify-content-end flex-column"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <p data-aos="fade-up" className="requirements-text">
              <span>{title}</span>{' '}
              <span style={{ color: '#221AFB' }}>
                {highlightedText.split('\n').map((text, i) => (
                  <Fragment key={i}>
                    {text}
                    {i === 0 && <br />}
                  </Fragment>
                ))}
              </span>
            </p>

            <div
              className="d-flex flex-wrap order-xs-2 order-xl-1"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="docs-required-header">{documentsTitle}</div>
              <div className="docs-required-content d-flex align-items-center">
                <div className="row w-100">
                  {documents.length > 1
                    ? documents.map((doc, index) => (
                        <div
                          key={index}
                          className={`col-md-6 ${index === 0 ? 'docs-subtext' : 'docs-subtext-2'}`}
                          style={{
                            backgroundColor: doc.backgroundColor
                          }}
                        >
                          {doc.title && (
                            <div
                              style={{
                                fontSize: '18px',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                marginTop: '0'
                              }}
                              dangerouslySetInnerHTML={{ __html: doc.title }}
                            />
                          )}

                          <div
                            style={{
                              display: 'flex',
                              // justifyContent: "center", // Center the subtext
                              alignItems: 'center', // Center vertically if necessary
                              height: '80%' // Ensure it takes up the full height available
                            }}
                          >
                            <ul>
                              <li dangerouslySetInnerHTML={{ __html: doc.text ?? '' }} />
                            </ul>
                          </div>
                        </div>
                      ))
                    : documents.map((doc, index) => (
                        <div
                          key={index}
                          className={`docs-subtext ${
                            index === 0 ? 'docs-subtext' : 'docs-subtext-2'
                          }`}
                          style={{
                            backgroundColor: doc.backgroundColor
                          }}
                        >
                          <div
                            style={{
                              fontSize: '18px',
                              display: 'flex',
                              justifyContent: 'flex-start',
                              marginTop: '0'
                            }}
                            dangerouslySetInnerHTML={{ __html: doc.text ?? '' }}
                          />
                        </div>
                      ))}
                </div>
              </div>
            </div>
          </Col>

          <Col
            xl={6}
            xs={12}
            className="order-xs-1 order-xl-2 eligibility-main-container d-flex flex-column justify-content-end"
            data-aos="fade-left"
            data-aos-delay="300"
          >
            <div className="docs-required-header text-start px-5">{eligibilityTitle}</div>
            <div className="eligibilty-content">
              {/* If employeeType is provided, it will be displayed here. Otherwise, this
              section will only show the age range and income requirements for eligibility. */}
              {employeeType && (
                <div className="eligibilty-subheader text-center">{employeeType}</div>
              )}
              <div className="eligibilty-age">
                <div className="age_border">
                  <span className="purecounter">{ageRange.minimum?.value}</span>
                  <span className="eligibility_age mx-2">{yearsOld}</span>
                </div>
                <div className="eligibility_age pt-2">{ageRange.minimum?.label}</div>
              </div>
              <div className="eligibilty-age middle">
                <div className="table-desktop">
                  <span
                    style={{
                      borderBottomColor: '#231BFC',
                      borderBottomWidth: '6px',
                      borderBottomStyle: 'solid',
                      color: 'transparent'
                    }}
                  >
                    ,,,,,
                  </span>
                </div>
                <div className="table-mobile">
                  <span
                    style={{
                      borderBottomColor: '#231BFC',
                      borderBottomWidth: '6px',
                      borderBottomStyle: 'solid',
                      color: 'transparent'
                    }}
                  >
                    ,,,
                  </span>
                </div>
                <div></div>
              </div>
              <div className="eligibilty-age">
                <div className="age_border">
                  <span className="purecounter">{ageRange.maximum?.value}</span>
                  <span className="eligibility_age mx-2">{yearsOld}</span>
                </div>
                <div className="eligibility_age pt-2">{ageRange.maximum?.label}</div>
              </div>
            </div>
            <div className="eligibilty-content-2 p-5">
              {tagline2 && <div className="eligibilty-subheader-2 text-center">{tagline2}</div>}
              <div className="eligibilty-income">
                <div>
                  <span style={{ fontSize: '48px' }}>{income.range}</span>
                </div>
                <div className="eligibility_income">{income.description}</div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  )
}

export default Eligibility
